import { autoinject } from 'aurelia-framework';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { ValidationHelpers } from '../../services/validation-helpers';
import { I18N } from 'aurelia-i18n';

@autoinject
export class SecuritySettings {
	model: any;
	securityQuestion1List: any;
	securityQuestion2List: any;
	errorMessage: any;
	validationErrors: any;
	customer: any;
	donotshow: any;
	successItem: any;
	contactInfo: any;
	isInvoiceEnabled: boolean;

	updateUsernameDesc: string;
	updatePasswordDesc: string;
	updateQuestion1Desc: string;
	updateQuestion2Desc: string;

	constructor(
		private payrazrService: PayrazrService,
		private globalState: State,
		private i18n: I18N) {

		this.model = {};
		this.securityQuestion1List = [];
		this.securityQuestion2List = [];
		this.errorMessage = '';
		this.validationErrors = [];
		this.customer = {};
		this.customer.paymentMethods = {};
		this.donotshow = false;
		this.isInvoiceEnabled = globalState.business.portalSettings.businessSettings.isInvoiceEnabled;

		this.updateUsernameDesc = this.i18n.i18next.t('updateUsernameDesc', 'Update Username');
		this.updatePasswordDesc = this.i18n.i18next.t('updatePassword', 'Update Password');
		this.updateQuestion1Desc = this.i18n.i18next.t('updateQuestion1Desc', 'Update Security Question 1');
		this.updateQuestion2Desc = this.i18n.i18next.t('updateQuestion2Desc', 'Update Security Question 2');
	}

	activate() {
		this.payrazrService.refreshGlobalCustomer().then(response => {
			this.model = new Model(this.globalState.customer);
			this.securityQuestion1List = this.globalState.question1List;
			this.securityQuestion2List = this.globalState.question2List;
		}).catch(err => {
			console.error('Failed to refresh customer information.');
		});

		this.getCustomerPaymentMethods();
	}

	updateUsername() {
		this.successItem = '';
		this.errorMessage = '';
		this.validationErrors = [];

		let validator = new UsernameValidator(this.i18n);
		if (validator.hasErrors(this.model)) {
			this.validationErrors = validator.errors;
		} else {
			this.payrazrService.updateUsername(this.model.username)
				.then(() => {
					// if Success update the cached username.
					this.model.customer.securitySettings.username = this.model.username;
					this.successItem = this.successItem = this.i18n.i18next.t('username', 'Username');
				})
				.catch(error => {
					this.errorMessage = this.i18n.i18next.t('updateUsernameError', 'Error: Unable to update username');
				});
		}
	}

	updatePassword() {
		this.successItem = '';
		this.errorMessage = '';
		this.validationErrors = [];

		let validator = new PasswordValidator(this.i18n);
		if (validator.hasErrors(this.model)) {
			this.validationErrors = validator.errors;
		} else {
			this.payrazrService.updatePassword(this.model.currentPassword, this.model.newPassword)
				.then(() => {
					this.globalState.customer.updateSecuritySettings(this.model.username, this.model.question1, this.model.question2);
					this.successItem = this.i18n.i18next.t('password', 'Password');
				})
				.catch(response => {
					response
						.json()
						.then(error => {
							this.validationErrors.push(error.Message);
						});
				});
		}
	}

	updateSecurityQuestion(questionNum) {
		this.successItem = '';
		this.errorMessage = '';
		this.validationErrors = [];

		let validator = new QuestionValidator(this.i18n, questionNum);
		if (validator.hasErrors(this.model)) {
			this.validationErrors = validator.errors;
		} else {
			let question = questionNum === 1 ? this.model.question1 : this.model.question2;
			let answer = questionNum === 1 ? this.model.answer1 : this.model.answer2;
			this.payrazrService.updateSecurityQuestion(questionNum, question, answer)
				.then(() => {
					this.globalState.customer.updateSecuritySettings(this.model.username, this.model.question1, this.model.question2);
					this.successItem = this.i18n.i18next.t('securityQuestionAnswerX', { defaultValue: `Security question/answer ${questionNum}`, questionNumber: questionNum });
				})
				.catch(response => {
					response
						.json()
						.then(err => {
							this.errorMessage = this.i18n.i18next.t('updateUnsuccessfulError', { defaultValue: `Update was unsuccessful: ${err}`, error: err });
						});
				});
		}
	}

	getCustomerPaymentMethods() {
		this.payrazrService.getCustomerPaymentMethodsByCustomerId().then(response => {
			if (response.isSuccessful) {
				this.customer.paymentMethods = response.entity;
			} else {
				console.error(response.message);
			}
		}
		).catch(err => {
			console.error('Failed to retrieve customer payment methods');
		});
	}
}

class Model {
	customer: any;
	username: any;
	currentPassword: any;
	newPassword: any;
	question1: any;
	question2: any;
	answer1: any;
	answer2: any;
	isEmailOptedIn: boolean;
	isPaperStatementsOptedIn: boolean;
	isEmailStatementsOptedIn: boolean;
	email: any;
	homePhone: any;
	mobilePhone: any;
	workPhone: any;
	smsIsOptedIn: boolean;

	constructor(customer) {
		this.customer = customer;

		this.username = customer.securitySettings.username;

		this.currentPassword = '';
		this.newPassword = '';

		this.question1 = customer.securitySettings.question1;
		this.question2 = customer.securitySettings.question2;

		this.answer1 = '';
		this.answer2 = '';

		this.isPaperStatementsOptedIn = customer.documentSettings.isPaperOptedIn;
		this.isEmailStatementsOptedIn = customer.documentSettings.isEmailOptedIn;

		this.email = customer.email;

		this.homePhone = customer.homePhone;
		this.mobilePhone = ValidationHelpers.sanitizePhoneNumber(customer.mobilePhone);
		this.workPhone = customer.workPhone;

		// Unless the customer explicitly opts out, they are opted in.
		this.smsIsOptedIn = customer.notificationSettings.smsIsOptedIn === undefined ? true : customer.notificationSettings.smsIsOptedIn;
	}
}

class UsernameValidator {
	errors: any;

	constructor(private i18n: I18N) {
		this.errors = [];
	}

	hasErrors(model) {
		if (model.username === model.customer.securitySettings.username) {
			this.addError(this.i18n.i18next.t('currentUsernameError', 'That is the same as your current username'));
		}

		if (model.username.length < 6 || model.username.length > 50) {
			this.addError(this.i18n.i18next.t('usernameCharacterLengthError', 'Username must be between 6 and 50 characters in length'));
		}

		const usernameRegex = /^[a-zA-Z0-9._@]+$/;
		if (!usernameRegex.test(model.username)) {
			this.addError(this.i18n.i18next.t('usernameCanOnlyContainCertainCharacters', 'Username can only contain letters, numbers and the following special characters: _ . @'));
		}

		return this.errors.length > 0;
	}

	addError(e) {
		this.errors.push(e);
	}
}

class PasswordValidator {
	errors: any;

	constructor(private i18n: I18N) {
		this.errors = [];
	}

	hasErrors(model) {
		if (model.currentPassword.length === 0) {
			this.addError(this.i18n.i18next.t('currentPasswordError', 'Please enter your current password'));
		}
		if (model.newPassword.length === 0) {
			this.addError(this.i18n.i18next.t('newPasswordConfirmPasswordError', 'If you are changing your password, you must fill both the new password and confirm password fields'));
		} else if (model.newPassword !== model.confirmPassword) {
			this.addError(this.i18n.i18next.t('passwordMatchError', 'The new passwords do not match'));
		} else if (model.newPassword.length < 12) {
			this.addError(this.i18n.i18next.t('passwordsMustBeTwelveOrMoreCharacters', 'Password must be a minimum of 12 characters.'));
		} else if (!ValidationHelpers.isStrongPassword(model.newPassword)) {
			this.addError(this.i18n.i18next.t('newPasswordRequirementError', 'New password does not meet requirements'));
		}

		return this.errors.length > 0;
	}

	addError(e) {
		this.errors.push(e);
	}
}

class QuestionValidator {
	errors: any;
	questionNumber: any;

	constructor(private i18n: I18N, questionNumber) {
		this.errors = [];
		this.questionNumber = questionNumber;
	}

	hasErrors(model) {
		if (this.questionNumber === 1) {
			if (model.answer1.length < 3 || model.answer1.length > 50) {
				this.addError(this.i18n.i18next.t('securityQuestion1Error', 'The answer to the 1st security question must be between 3 and 50 characters long'));
			}
		}

		if (this.questionNumber === 2) {
			if (model.answer2.length < 3 || model.answer2.length > 50) {
				this.addError(this.i18n.i18next.t('securityQuestion2Error', 'The answer to the 2nd security question must be between 3 and 50 characters long'));
			}
		}

		if (model.question1 === model.question2) {
			this.addError(this.i18n.i18next.t('securityQuestionSameError', 'Please select 2 different security questions'));
		}

		return this.errors.length > 0;
	}

	addError(e) {
		this.errors.push(e);
	}
}
